/**
 * Telling a person from a link scanner after a tracked email link (a Gmail invite's button, a notification or
 * campaign email's link).
 *
 * Mail security services open the links in an email before anyone reads it, some from home networks, and their
 * browsers load the page and leave. So the click report carries what the browser says about itself, and the first
 * real tap, click, key or scroll afterwards is reported once per invite or click: that is what marks a person.
 */
import { gmailInviteService, type InviteBrowserSignals } from '../services/gmailInviteService';

const USED_KEY = 'gmail_invite_used';
const FIRST_USE_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;
const INPUTS: Record<string, string> = { pointerdown: 'pointer', keydown: 'key', touchstart: 'touch', wheel: 'wheel' };

type ExtendedNavigator = typeof navigator & {
  deviceMemory?: number;
  userAgentData?: { brands?: Array<{ brand: string }> };
};

const watching = new Set<string>();

const timezone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

/** What the browser says about itself; nothing here identifies the person. */
export const browserSignals = (extra: Partial<InviteBrowserSignals> = {}): InviteBrowserSignals => {
  const nav = navigator as ExtendedNavigator;
  const brands = (nav.userAgentData?.brands ?? []).map((item) => item.brand).join(' ');

  return {
    webdriver: nav.webdriver === true,
    headless: /Headless/i.test(`${nav.userAgent} ${brands}`),
    languages: Array.from(nav.languages ?? []).slice(0, 5),
    timezone: timezone(),
    screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    touch_points: nav.maxTouchPoints ?? 0,
    cores: nav.hardwareConcurrency || undefined,
    memory_gb: nav.deviceMemory,
    plugins: nav.plugins?.length,
    visible: document.visibilityState === 'visible',
    focused: document.hasFocus(),
    ...extra,
  };
};

const alreadyUsed = (token: string): boolean => {
  try {
    return localStorage.getItem(USED_KEY) === token;
  } catch {
    return false;
  }
};

const markUsed = (token: string) => {
  try {
    localStorage.setItem(USED_KEY, token);
  } catch {
    // Blocked storage: the report still goes once for this page.
  }
};

/** Report a first use of the site after this invite's button: once per invite and browser. */
export const reportFirstUse = (token: string, input: string): void => {
  if (alreadyUsed(token)) return;
  markUsed(token);
  const signals = browserSignals({ input, after_ms: Math.round(window.performance.now()) });

  void gmailInviteService.reportUse(token, signals).catch(() => undefined);
};

/** Wait for the first real tap, click, key or scroll on this page, then call ``onUse`` with what it was: once per
 * ``key`` (an invite, an email click). */
export const onFirstUse = (key: string, onUse: (input: string) => void): void => {
  if (!key || watching.has(key)) return;
  watching.add(key);

  const listener = (event: Event) => {
    // Script-made events (a scanner's, or a page's own) are not a person.
    if (!event.isTrusted) return;
    FIRST_USE_EVENTS.forEach((name) => window.removeEventListener(name, listener, true));
    onUse(INPUTS[event.type]);
  };

  FIRST_USE_EVENTS.forEach((name) => window.addEventListener(name, listener, { capture: true, passive: true }));
};

/** Wait for the first real tap, click, key or scroll after this invite's button, and report it. */
export const watchFirstUse = (token: string): void => {
  if (!token || alreadyUsed(token)) return;
  onFirstUse(`invite:${token}`, (input) => reportFirstUse(token, input));
};
